import { useEffect, useState } from "react";
import { Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../api";

const GOLD_TOAST = { background: "#1A1A1A", color: "#D4AF37", border: "1px solid rgba(212,175,55,0.4)" };
const inputCls = "w-full bg-[#121212] border border-white/10 rounded-lg px-2.5 py-1.5 text-[11px] focus:outline-none focus:border-[#D4AF37]/60";

const ItemEditor = ({ item, onSaved, onDeleted }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);

  const start = () => {
    setF({
      name: item.name, price: String(item.price), description: item.description || "",
      image: item.image || "", vegetarian: !!item.vegetarian, active: item.active !== false,
      story: item.story || "",
      quiz_q: item.quiz?.question || "", quiz_opts: item.quiz?.options || ["", "", "", ""],
      quiz_ans: item.quiz?.answer ?? 0,
    });
    setOpen(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      const body = {
        name: f.name, price: Number(f.price), description: f.description, image: f.image,
        vegetarian: f.vegetarian, active: f.active, story: f.story,
      };
      if (f.quiz_q.trim()) body.quiz = { question: f.quiz_q, options: f.quiz_opts, answer: Number(f.quiz_ans) };
      const r = await api.patch(`/admin/brand-items/${item.item_id}`, body);
      onSaved(r.data);
      setOpen(false);
      toast.success("Item saved — story & quiz back in DRAFT until approved", { style: GOLD_TOAST });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save the item"); }
    setBusy(false);
  };

  const approve = async () => {
    setBusy(true);
    try {
      const r = await api.patch(`/admin/brand-items/${item.item_id}`, { story_status: "approved" });
      onSaved(r.data);
      toast.success("Item story & quiz approved — now live for guests", { style: GOLD_TOAST });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not approve"); }
    setBusy(false);
  };

  const remove = async () => {
    if (!window.confirm(`Remove "${item.name}" from the menu permanently?`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/brand-items/${item.item_id}`);
      onDeleted(item.item_id);
      toast.success("Item removed", { style: GOLD_TOAST });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not remove"); }
    setBusy(false);
  };

  return (
    <div className="rounded-xl bg-[#1A1A1A] border border-white/5 p-2" data-testid={`admin-item-${item.item_id}`}>
      <div className="flex items-center gap-2">
        {item.image ? (
          <img src={item.image} alt={item.name} className="w-11 h-11 rounded-lg object-cover shrink-0 border border-white/10"
            onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} />
        ) : (
          <span className="w-11 h-11 rounded-lg bg-white/5 shrink-0 flex items-center justify-center text-[8px] text-white/30">NO PHOTO</span>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-1.5">
            <p className="text-[11px] font-bold truncate min-w-0">{item.name}</p>
            <span className="text-[11px] font-bold text-[#D4AF37] shrink-0">${Number(item.price).toFixed(2)}</span>
          </div>
          <p className="text-[9px] tracking-widest font-bold mt-0.5">
            {item.active === false && <span className="text-white/40 mr-2">HIDDEN</span>}
            {item.story ? (
              item.story_status === "approved"
                ? <span className="text-[#D4AF37]" data-testid={`item-approved-${item.item_id}`}>STORY & QUIZ LIVE</span>
                : <span className="text-[#E25822]" data-testid={`item-draft-${item.item_id}`}>STORY DRAFT — REVIEW</span>
            ) : <span className="text-white/35">NO STORY YET</span>}
          </p>
        </div>
        {item.story && item.story_status !== "approved" && (
          <button data-testid={`item-approve-${item.item_id}`} onClick={approve} disabled={busy}
            className="shrink-0 text-[9px] font-black tracking-widest text-black bg-[#D4AF37] rounded-full px-2.5 py-1.5 disabled:opacity-40">APPROVE</button>
        )}
        <button data-testid={`item-edit-${item.item_id}`} onClick={() => (open ? setOpen(false) : start())}
          className="shrink-0 text-[9px] font-bold tracking-widest text-white/60 border border-white/20 rounded-full px-2.5 py-1.5">
          {open ? "CLOSE" : "EDIT"}
        </button>
      </div>
      {open && f && (
        <div className="mt-2 space-y-2" data-testid={`item-editor-${item.item_id}`}>
          <div className="flex gap-2">
            <input data-testid={`item-name-${item.item_id}`} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inputCls} placeholder="Name" />
            <input data-testid={`item-price-${item.item_id}`} value={f.price} onChange={(e) => setF({ ...f, price: e.target.value.replace(/[^0-9.]/g, "") })}
              className={`${inputCls} !w-20 shrink-0 text-right`} placeholder="9.99" />
          </div>
          <input data-testid={`item-desc-${item.item_id}`} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className={inputCls} placeholder="Short description" />
          <input data-testid={`item-image-${item.item_id}`} value={f.image} onChange={(e) => setF({ ...f, image: e.target.value })} className={inputCls} placeholder="Photo — https://… or /brands/…jpg" />
          <div className="flex gap-3">
            <label className="flex items-center gap-1.5 text-[10px] text-white/60">
              <input type="checkbox" data-testid={`item-veg-${item.item_id}`} checked={f.vegetarian} onChange={(e) => setF({ ...f, vegetarian: e.target.checked })} /> VEGETARIAN
            </label>
            <label className="flex items-center gap-1.5 text-[10px] text-white/60">
              <input type="checkbox" data-testid={`item-active-${item.item_id}`} checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} /> VISIBLE TO GUESTS
            </label>
          </div>
          <textarea data-testid={`item-story-${item.item_id}`} value={f.story} onChange={(e) => setF({ ...f, story: e.target.value })} rows={4}
            className={`${inputCls} rounded-xl`} placeholder="Item story guests will read (20+ characters)" />
          <p className="text-[9px] tracking-widest text-[#D4AF37] font-bold">QUIZ QUESTION (+5 / −5 PTS)</p>
          <input data-testid={`item-quiz-q-${item.item_id}`} value={f.quiz_q} onChange={(e) => setF({ ...f, quiz_q: e.target.value })} className={inputCls} placeholder="Question" />
          {f.quiz_opts.map((o, i) => (
            <div key={i} className="flex items-center gap-2">
              <input data-testid={`item-quiz-opt-${item.item_id}-${i}`} value={o}
                onChange={(e) => setF({ ...f, quiz_opts: f.quiz_opts.map((x, j) => (j === i ? e.target.value : x)) })}
                className={inputCls} placeholder={`Option ${i + 1}`} />
              <label className="flex items-center gap-1 text-[9px] text-white/50 shrink-0">
                <input type="radio" name={`ans-${item.item_id}`} checked={Number(f.quiz_ans) === i} onChange={() => setF({ ...f, quiz_ans: i })} /> CORRECT
              </label>
            </div>
          ))}
          <div className="flex gap-2">
            <button data-testid={`item-save-${item.item_id}`} onClick={save} disabled={busy}
              className="bg-[#D4AF37] text-black text-[9px] font-black rounded-full px-4 py-2 tracking-widest disabled:opacity-50">
              {busy ? <Loader2 size={10} className="animate-spin" /> : "SAVE ITEM"}
            </button>
            <button onClick={() => setOpen(false)} className="text-[9px] font-bold tracking-widest text-white/50 border border-white/20 rounded-full px-4 py-2">CANCEL</button>
            <button data-testid={`item-delete-${item.item_id}`} onClick={remove} disabled={busy}
              className="ml-auto text-[9px] font-bold tracking-widest text-[#E25822] border border-[#E25822]/40 rounded-full px-4 py-2">DELETE</button>
          </div>
        </div>
      )}
    </div>
  );
};

const AddItemForm = ({ brand, onAdded }) => {
  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const [desc, setDesc] = useState("");
  const [veg, setVeg] = useState(true);
  const [busy, setBusy] = useState(false);

  const add = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/brand-items", {
        brand, name, price: Number(price), description: desc, vegetarian: veg,
      });
      onAdded(r.data);
      setName(""); setPrice(""); setDesc("");
      toast.success("Item added — give it a story, then approve it", { style: GOLD_TOAST });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add the item"); }
    setBusy(false);
  };

  return (
    <div className="rounded-xl border border-dashed border-[#D4AF37]/30 p-2 space-y-2" data-testid={`add-item-form-${brand}`}>
      <div className="flex gap-2">
        <input data-testid={`add-item-name-${brand}`} value={name} onChange={(e) => setName(e.target.value)} className={inputCls} placeholder="New item name" />
        <input data-testid={`add-item-price-${brand}`} value={price} onChange={(e) => setPrice(e.target.value.replace(/[^0-9.]/g, ""))}
          className={`${inputCls} !w-20 shrink-0 text-right`} placeholder="9.99" />
      </div>
      <div className="flex gap-2 items-center">
        <input data-testid={`add-item-desc-${brand}`} value={desc} onChange={(e) => setDesc(e.target.value)} className={inputCls} placeholder="Short description (optional)" />
        <label className="flex items-center gap-1 text-[9px] text-white/50 shrink-0">
          <input type="checkbox" checked={veg} onChange={(e) => setVeg(e.target.checked)} /> VEG
        </label>
        <button data-testid={`add-item-btn-${brand}`} onClick={add} disabled={busy || !name.trim() || !price}
          className="shrink-0 bg-[#D4AF37] text-black text-[9px] font-black rounded-full px-3.5 py-2 tracking-widest disabled:opacity-40">ADD ITEM</button>
      </div>
    </div>
  );
};

export const AdminBrands = () => {
  const [brands, setBrands] = useState(null);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(null);
  const [itemsOpen, setItemsOpen] = useState(null);
  const [items, setItems] = useState({});

  useEffect(() => {
    api.get("/admin/brands").then((r) => setBrands(r.data)).catch(() => {});
  }, []);

  const patchLocal = (b) => setBrands(brands.map((x) => (x.key === b.key ? b : x)));

  const approve = async (b) => {
    setBusy(b.key);
    try {
      const r = await api.put(`/admin/brands/${b.key}`, { story_status: "approved" });
      patchLocal(r.data);
      toast.success(`${b.name} story approved — the draft chip is gone for guests`, { style: GOLD_TOAST });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not approve"); }
    setBusy(null);
  };

  const save = async (b) => {
    setBusy(`save-${b.key}`);
    try {
      const r = await api.put(`/admin/brands/${b.key}`, { story: draft });
      patchLocal(r.data);
      setEditing(null);
      toast.success("Story saved — back in DRAFT until you approve it", { style: GOLD_TOAST });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    setBusy(null);
  };

  const openItems = async (b) => {
    if (itemsOpen === b.key) return setItemsOpen(null);
    setItemsOpen(b.key);
    try {
      const r = await api.get(`/admin/brand-items/${b.key}`);
      setItems((m) => ({ ...m, [b.key]: r.data }));
    } catch { /* item list unavailable */ }
  };

  const setItemsFor = (bkey, fn) => setItems((m) => ({ ...m, [bkey]: fn(m[bkey] || []) }));

  const approveAll = async (b) => {
    setBusy(`all-${b.key}`);
    try {
      const r = await api.post(`/admin/brands/${b.key}/approve-items`);
      const rr = await api.get(`/admin/brand-items/${b.key}`);
      setItems((m) => ({ ...m, [b.key]: rr.data }));
      toast.success(`${r.data.approved} item stories approved — live for guests`, { style: GOLD_TOAST });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not approve items"); }
    setBusy(null);
  };

  if (!brands) return <p className="text-xs text-white/40">Loading…</p>;

  return (
    <div className="space-y-3" data-testid="admin-brands">
      <p className="text-xs text-white/50">
        Brand storylines and item stories shown to guests. Edits always return to <span className="text-[#E25822] font-bold">DRAFT — REQUIRES REVIEW</span> until you approve them.
      </p>
      {brands.map((b) => (
        <div key={b.key} data-testid={`admin-brand-${b.key}`} className="rounded-xl bg-[#121212] border border-white/10 p-4">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <p className="text-sm font-bold" style={{ color: b.accent }}>{b.name}</p>
              <p className="text-[10px] text-white/40">{b.tagline} · {b.orderable ? "ORDERABLE" : "BROWSE ONLY"}</p>
            </div>
            {b.story_status === "approved" ? (
              <span data-testid={`brand-approved-${b.key}`} className="flex items-center gap-1 text-[9px] font-black tracking-widest text-black bg-[#D4AF37] rounded-full px-3 py-1.5">
                <CheckCircle2 size={10} /> APPROVED
              </span>
            ) : (
              <button data-testid={`brand-approve-${b.key}`} onClick={() => approve(b)} disabled={busy === b.key}
                className="flex items-center gap-1 text-[9px] font-black tracking-widest text-[#E25822] border border-[#E25822]/60 rounded-full px-3 py-1.5 disabled:opacity-50">
                {busy === b.key ? <Loader2 size={10} className="animate-spin" /> : null} DRAFT — APPROVE NOW
              </button>
            )}
          </div>
          {editing === b.key ? (
            <div className="mt-3">
              <textarea data-testid={`brand-story-input-${b.key}`} value={draft} onChange={(e) => setDraft(e.target.value)} rows={6}
                className="w-full bg-[#1A1A1A] border border-white/10 rounded-xl px-3 py-2 text-xs focus:outline-none focus:border-[#D4AF37]/60" />
              <div className="flex gap-2 mt-2">
                <button data-testid={`brand-story-save-${b.key}`} onClick={() => save(b)} disabled={busy === `save-${b.key}`}
                  className="bg-[#D4AF37] text-black text-[9px] font-black rounded-full px-4 py-2 tracking-widest disabled:opacity-50">SAVE STORY</button>
                <button onClick={() => setEditing(null)} className="text-[9px] font-bold tracking-widest text-white/50 border border-white/20 rounded-full px-4 py-2">CANCEL</button>
              </div>
            </div>
          ) : (
            <>
              <p className="text-[11px] text-white/55 mt-2 whitespace-pre-line line-clamp-3">{b.story}</p>
              <button data-testid={`brand-story-edit-${b.key}`} onClick={() => { setEditing(b.key); setDraft(b.story || ""); }}
                className="mt-2 text-[9px] font-bold tracking-widest text-white/60 border border-white/20 rounded-full px-3.5 py-1.5">EDIT STORY</button>
            </>
          )}
          {b.key !== "turban" && (
            <div className="mt-2">
              <div className="flex gap-2 flex-wrap">
                <button data-testid={`brand-items-toggle-${b.key}`} onClick={() => openItems(b)}
                  className="text-[9px] font-bold tracking-widest text-[#D4AF37]/80 border border-[#D4AF37]/30 rounded-full px-3.5 py-1.5">
                  {itemsOpen === b.key ? "HIDE ITEMS" : "ITEMS · MENU & PHOTOS"}
                </button>
                {itemsOpen === b.key && (
                  <button data-testid={`approve-all-items-${b.key}`} onClick={() => approveAll(b)} disabled={busy === `all-${b.key}`}
                    className="text-[9px] font-black tracking-widest text-black bg-[#D4AF37] rounded-full px-3.5 py-1.5 disabled:opacity-50">
                    APPROVE ALL ITEM STORIES
                  </button>
                )}
              </div>
              {itemsOpen === b.key && (
                <div className="mt-3 space-y-2" data-testid={`brand-items-list-${b.key}`}>
                  {(items[b.key] || []).map((it) => (
                    <ItemEditor key={it.item_id} item={it}
                      onSaved={(ni) => setItemsFor(b.key, (list) => list.map((x) => (x.item_id === ni.item_id ? ni : x)))}
                      onDeleted={(id) => setItemsFor(b.key, (list) => list.filter((x) => x.item_id !== id))} />
                  ))}
                  <AddItemForm brand={b.key} onAdded={(ni) => setItemsFor(b.key, (list) => [...list, ni])} />
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
